import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, TemplateRef, ViewChild, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, catchError, distinctUntilChanged, finalize, forkJoin, interval, map, of, takeUntil, takeWhile } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { Loading } from '@components/ui/loading/loading';
import { BackButton } from '@components/ui/back-button/back-button';
import { TabItem, TabsDynamic } from '@components/ui/tabs/tabs-dynamic';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import {
  AssignmentAuditDetailsResponse,
  AssignmentProcessService as AssignmentProcessApiService,
  BackgroundJobExecutionDetailsResponse,
  CommandExecutionResponse,
  ExternalServiceCallDetails,
  MonitoredOperationListItemResponse,
  OperationLogRecordResponse,
  OperationMonitoringService as OperationMonitoringApiService,
} from '@6grain/masofa-monolith-api-client/common';
import { ExternalServiceCallHistoryService } from '@6grain/masofa-monolith-api-client/common/api/externalServiceCallHistory.service';
import { AiModelService } from '@6grain/masofa-monolith-api-client/common/api/aiModel.service';
import { OnnxAssignmentModel, readOnnxAssignmentModel } from './onnx-assignment-model';
import { RegionService } from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { ClearButton } from '@components/button/clear-button/clear-button';
import { FormBuilder, IMsfFormBuilder } from '@components/form-builder/form-builder';
import { adminHashesEqual } from '@app/pages/admin/admin-hash-state';
import {
  canonicalizeOperationDetailsTab,
  operationDetailsTabFromHash,
  operationDetailsTabHash,
  operationDetailsUrl,
} from '@app/pages/admin/operation-details.util';
import { developerCatalogCommandLink } from '@app/pages/admin/developer-catalog-page/developer-catalog.utils';
import { isCodeCatalogEnabled } from '@app/utils/environment-features';
import { assignmentHostLabel, readAssignmentHost, readAssignmentWorker } from '@app/pages/admin/assignment-host.util';
import { AssignmentHostLabelComponent } from '@app/pages/admin/components/assignment-host-label/assignment-host-label';
import {
  AssignmentDataRecordGroup,
  ProcessDataEffect,
  assignmentDataRecordBytesLabel,
  assignmentDataRecordCount,
  composeAssignmentDataRecords,
  dataEffectOperationSeverity,
  flattenAssignmentDataRecords,
  hasInProgressSatelliteDownload,
  normalizeProcessDataEffects,
  syncAssignmentDataRecordRows,
} from '@app/pages/admin/orchestration-data-records.util';
import {
  ProcessStorageEffect,
  assignmentStorageBytesLabel,
  flattenAssignmentStorageRecords,
  normalizeProcessStorageEffects,
  readAssignmentStorageRecords,
} from '@app/pages/admin/orchestration-object-storage.util';
import {
  AssignmentDataRecordFilterModel,
  buildAssignmentDataRecordFilterFields,
  emptyAssignmentDataRecordFilter,
  filterAssignmentDataRecords,
  syncAssignmentDataRecordFilterOptions,
} from '@app/pages/admin/orchestration-data-records-filter.util';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import {
  AssignmentFollowUp,
  assignmentFollowUps,
  assignmentOverviewFiles,
  assignmentOverviewTables,
  AssignmentOverviewFile,
  AssignmentOverviewTable,
} from '@app/pages/admin/assignment-overview.util';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { CopyableLogFieldComponent } from '@app/pages/admin/components/copyable-log-field/copyable-log-field';
import { JsonTreeComponent } from '@app/pages/admin/components/json-tree/json-tree';
import { notificationJournalColumns, notificationJournalTableConfig } from '@app/pages/admin/notification-journal-table.util';
import { NotificationDeliveryDto, UserNotificationsApiService } from '@app/services/user-notifications-api.service';
import { MakeSubscriptionDialogComponent } from '@app/pages/admin/components/make-subscription-dialog/make-subscription-dialog';
import { defaultSubscriptionNameFromAssignment } from '@app/pages/admin/components/make-subscription-dialog/make-subscription-dialog.util';
import {
  formatOperationLogsForCopy,
  joinCopyableLogParts,
} from '@app/pages/admin/components/copyable-log-field/copyable-log-field.util';
import { LiveStatusDurationComponent } from './live-status-duration/live-status-duration';
import { processKeysMatch } from '@app/pages/admin/orchestration-process-keys.util';
import {
  namedKeyLabel,
  orchestrationEventUrl,
  orchestrationProcessUrl,
  orchestrationSubscriptionUrl,
  publishedEventIdentifierLabel,
  readSatelliteProductId,
  satelliteProductAnalysisUrlFromEvent,
  satelliteProductAnalysisUrlFromJson,
} from '@app/pages/admin/orchestration-identity.util';
import { formatArchiveSizeGiB, readSatelliteArchiveSizes, readSatelliteOverviewParameters, satelliteMeasureBacklogSizeRows } from '@app/pages/admin/orchestration-satellite-size.util';
import { toRecordPageHeading } from '@app/pages/admin/orchestration-record-heading.util';
import { assignmentHasSatelliteProducts, assignmentStartApiError, SATELLITE_MEASURE_DOWNLOAD_BACKLOG_KEY } from '@app/pages/admin/assignment-start.util';
import {
  canCancelAssignment,
  canRestartAssignment,
} from '@app/pages/admin/orchestration-process-page/orchestration-process-page.util';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { ServerTranslateService } from '@app/services/server-translate.service';
import { extractMonitoredOutput, prettyJson } from '../../monitored-output.util';
import { externalCallProxy } from '@app/pages/admin/external-call-proxy';
import { NOTIFY_PEOPLE_PATH, notifyPeopleQueryParams } from '@app/pages/admin/orchestration-notify-people.util';
import {
  ExternalCallTreeLeaf,
  RequestTreeNode,
  applyRequestTreeExpand,
  buildRequestStepTree,
  countCompletedRequestSteps,
  collectExpandableIds,
  findRequestTreeNode,
  externalCallUrlWithoutQuery,
  formatHttpQueryParametersJson,
  formatHttpRequestLine,
  hasCapturedHttpBody,
  toggleTreeId,
} from './external-call-tree.util';

export type OperationKind = NonNullable<MonitoredOperationListItemResponse['kind']>;
type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

interface SatelliteParameterRow {
  label: string;
  value: string;
}

interface SatelliteRegionRow {
  id: string;
  name: string;
}

interface CommandView {
  id: string;
  stepKey: string;
  commandType: string;
  commandTypeShort: string;
  attempt: number | string;
  state: string;
  severity: TagSeverity;
  startedAt?: string | Date;
  duration: string;
  workerId: string;
  inputJson: string;
  resultJson: string;
  logs: string[];
  error: string | null;
  parentCommandId: string | null;
}

interface AssignmentChainLink {
  id: string;
  title: string;
  detail: string;
  state: string;
  severity: TagSeverity;
  kind: 'parent' | 'current' | 'child' | 'event';
  url: string | null;
  detailUrl?: string | null;
  hostLabel?: string | null;
  workerId?: string | null;
  workerServiceName?: string | null;
  workerHostName?: string | null;
}

interface AssignmentFollowUpView extends AssignmentFollowUp {
  severity: TagSeverity;
}

interface PublishedEventView {
  id: string;
  kind: string;
  eventKey: string;
  eventUrl: string | null;
  eventId: string;
  identifierLabel: string;
  triggerName: string;
  state: string;
  severity: TagSeverity;
  startedAt?: string | Date;
  completedAt?: string | Date;
  targetAssignmentId: string | null;
  targetAssignmentUrl: string | null;
  parametersJson: string | null;
  error: string | null;
  productAnalysisUrl: string | null;
}

interface StateHistoryView {
  id: string;
  state: string;
  severity: TagSeverity;
  changedAt?: string | Date;
  details: string | null;
}

interface SelectedCommandDetail {
  inputJson: string;
  resultJson: string;
  logs: string[];
  logText: string;
  attempt: number | string;
  startedAt?: string | Date;
  duration: string;
  workerId: string;
  commandTypeShort: string;
  error: string | null;
  satelliteProductUrl: string | null;
  satelliteProductId: string | null;
}

interface SelectedCallIo {
  requestHeaders: string;
  requestBody: string | null;
  requestText: string;
  responseHeaders: string;
  responseBody: string | null;
  responseText: string;
  error: string | null;
  proxyLabel: string;
}

interface DataEffectRow extends ProcessDataEffect {
  commandLabel: string;
  commandUrl: string[] | null;
  operationSeverity: TagSeverity;
}

interface StorageEffectRow extends ProcessStorageEffect {
  commandLabel: string;
  commandUrl: string[] | null;
  operationSeverity: TagSeverity;
}

interface AssignmentDataRecordRow {
  id: string;
  label: string | null;
  state: string | null;
  stateLabel: string | null;
  stateSeverity: TagSeverity;
  operation: string;
  operationSeverity: TagSeverity;
  live: boolean;
  statusUntil: string | null;
  bytes: number | null;
  bytesLabel: string;
  changedAt?: string | null;
  database: string;
  table: string;
  kind: string;
  productAnalysisUrl: string | null;
  fileDownloadUrl: string | null;
}

interface OperationLogView {
  timestamp?: string | Date;
  level: string;
  severity: TagSeverity;
  serviceName: string;
  loggerName: string | null;
  message: string | null;
  traceId: string | null;
  exception: string | null;
}

@Component({
  selector: 'app-operation-details-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    TagModule,
    Loading,
    BackButton,
    DateFormatLangPipe,
    PrimaryButton,
    SecondaryButton,
    TabsDynamic,
    DataTableComponent,
    FormBuilder,
    ClearButton,
    RouterModule,
    LiveStatusDurationComponent,
    CopyableIdentifierComponent,
    OrchestrationTableHeadingComponent,
    CopyableLogFieldComponent,
    JsonTreeComponent,
    MakeSubscriptionDialogComponent,
    AssignmentHostLabelComponent,
  ],
  templateUrl: './operation-details-dialog.html',
  styleUrl: './operation-details-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
})
export class OperationDetailsDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly monitoringService = inject(OperationMonitoringApiService);
  private readonly externalCallsApi = inject(ExternalServiceCallHistoryService);
  private readonly aiModels = inject(AiModelService);
  private readonly assignments = inject(AssignmentProcessApiService);
  private readonly regions = inject(RegionService);
  private readonly serverTranslate = inject(ServerTranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly destroy$ = new Subject<void>();
  readonly subscriptionDialog = viewChild(MakeSubscriptionDialogComponent);
  readonly translate = inject(TranslateService);
  private readonly dateFormatLang = inject(DateFormatLangPipe);
  private readonly detailsClosed$ = new Subject<void>();
  private readonly detailsRequestCancelled$ = new Subject<void>();
  private readonly operationLogRequestCancelled$ = new Subject<void>();
  private readonly notificationRequestCancelled$ = new Subject<void>();
  private readonly notificationsApi = inject(UserNotificationsApiService);
  notificationRows: NotificationDeliveryDto[] = [];
  notificationsLoading = false;
  readonly notificationColumns = notificationJournalColumns();
  readonly notificationTableConfig = notificationJournalTableConfig('No assignment notifications');
  private readonly callDetailsCancelled$ = new Subject<void>();
  private readonly onnxModelCancelled$ = new Subject<void>();
  private readonly databaseRequestCancelled$ = new Subject<void>();
  private detailsSilentLoading = false;

  readonly logLevels = ['Trace', 'Debug', 'Information', 'Warning', 'Error', 'Critical'];

  childOperations: MonitoredOperationListItemResponse[] = [];
  selectedAssignment: AssignmentAuditDetailsResponse | null = null;
  selectedJob: (BackgroundJobExecutionDetailsResponse & { safeConfigJson?: string | null }) | null = null;
  requestStepTree: RequestTreeNode[] = [];
  selectedRowId: string | null = null;
  selectedCommandDetail: SelectedCommandDetail | null = null;
  expandedCallId: string | null = null;
  selectedCallDetails: ExternalServiceCallDetails | null = null;
  selectedCallIo: SelectedCallIo | null = null;
  callDetailsLoading = false;
  jobConfigJson = '—';
  jobResultJson = '—';
  jobResultTypeName: string | null = null;
  assignmentInputJson: string | null = null;
  assignmentResultJson: string | null = null;
  jobStateSeverity: TagSeverity = 'secondary';
  commandViews: CommandView[] = [];
  assignmentChain: AssignmentChainLink[] = [];
  eventViews: PublishedEventView[] = [];
  historyViews: StateHistoryView[] = [];
  isPostProcessingAssignment = false;
  assignmentStateLabel = '—';
  assignmentStateSeverity: TagSeverity = 'secondary';
  assignmentProcessKey = '—';
  assignmentProcessVersion: number | null = null;
  assignmentProcessUrl: string | null = null;
  showAssignmentProcessKeyLink = false;
  parentAssignmentId: string | null = null;
  satelliteParameterRows: SatelliteParameterRow[] = [];
  satelliteProductAnalysisUrl: string | null = null;
  sourceSubscriptionUrl: string | null = null;
  satelliteProductId: string | null = null;
  onnxModel: OnnxAssignmentModel | null = null;
  satelliteRegions: SatelliteRegionRow[] = [];
  satelliteRegionCount = '—';
  satelliteRegionsExpanded = false;
  detailsLoading = false;
  childrenLoading = false;
  copyVisible = false;
  canCancelCurrent = false;
  canRestartCurrent = false;
  cancellationVisible = false;
  cancellationSaving = false;
  restartVisible = false;
  restartSaving = false;
  pageTitleKey = 'Assignment details';
  pageSubtitle: string | null = null;
  executedOnLabel: string | null = null;
  executedOnService: string | null = null;
  executedOnHost: string | null = null;
  executedOnWorkerId: string | null = null;
  parentRoute = '/admin/orchestration/assignments';
  copySaving = false;
  errorMessage = '';
  operationLogViews: OperationLogView[] = [];
  operationLogsCopyText = '';
  operationLogsLoading = false;
  operationLogsAvailable = false;
  operationLogsProvider = '';
  operationLogsMessage = '';
  operationLogsExternalUrl: string | null = null;
  operationLogsHasMore = false;
  operationLogLevel = '';
  operationLogSearch = '';
  activeDetailsTab = 'overview';
  dataEffectRows: DataEffectRow[] = [];
  storageEffectRows: StorageEffectRow[] = [];
  storageRecordRows: Array<{
    id: string;
    path: string | null;
    objectKind: string | null;
    bytes: number;
    createdAt: string;
    bucket: string;
    bytesLabel: string;
    downloadUrl: string | null;
  }> = [];
  createdArtifactFiles: AssignmentOverviewFile[] = [];
  createdArtifactTables: AssignmentOverviewTable[] = [];
  followUpAssignments: AssignmentFollowUpView[] = [];
  storageBytesLabel = '—';
  dataRecordBytesLabel = '—';
  dataRecordGroups: AssignmentDataRecordGroup[] = [];
  dataRecordRows: AssignmentDataRecordRow[] = [];
  filteredDataRecordRows: AssignmentDataRecordRow[] = [];
  dataRecordFilterModel: AssignmentDataRecordFilterModel = emptyAssignmentDataRecordFilter();
  readonly dataRecordFilterFields: IMsfFormBuilder[] = buildAssignmentDataRecordFilterFields();
  dataRecordCount = 0;
  copiedCommandType: string | null = null;
  private copiedCommandTypeReset: ReturnType<typeof setTimeout> | null = null;
  satelliteDownloadInProgress = false;
  databaseLoading = false;
  dataEffectSearch = '';
  dataRecordSearch = '';
  storageEffectSearch = '';
  storageRecordSearch = '';
  eventSearch = '';
  detailsTabs: TabItem[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'request-steps', label: 'Request steps' },
    { id: 'logs', label: 'External logs' },
    { id: 'storage', label: 'Storage' },
  ];
  readonly dataEffectTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'No declared database actions',
  };
  readonly storageEffectTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'No declared object-storage actions',
  };
  readonly dataRecordTableConfig: TableConfig = {
    paginator: true,
    rows: 25,
    keepOrder: true,
    emptyMessage: 'No database records for this assignment',
  };
  readonly storageRecordTableConfig: TableConfig = {
    paginator: true,
    rows: 25,
    keepOrder: true,
    emptyMessage: 'No object-storage writes for this assignment',
  };
  dataEffectColumns: TableColumn[] = [];
  storageEffectColumns: TableColumn[] = [];
  dataRecordColumns: TableColumn[] = [];
  storageRecordColumns: TableColumn[] = [];
  @ViewChild('requestStepsTemplate', { static: true })
  private readonly requestStepsTemplate?: TemplateRef<unknown>;
  @ViewChild('overviewTemplate', { static: true })
  private readonly overviewTemplate?: TemplateRef<unknown>;
  @ViewChild('eventsTemplate', { static: true })
  private readonly eventsTemplate?: TemplateRef<unknown>;
  @ViewChild('notificationsTemplate', { static: true })
  private readonly notificationsTemplate?: TemplateRef<unknown>;
  @ViewChild('logsTemplate', { static: true })
  private readonly logsTemplate?: TemplateRef<unknown>;
  @ViewChild('databaseTemplate', { static: true })
  private readonly databaseTemplate?: TemplateRef<unknown>;
  @ViewChild('dataEffectCommandTemplate', { static: true })
  private readonly dataEffectCommandTemplate?: TemplateRef<unknown>;
  @ViewChild('dataEffectOperationTemplate', { static: true })
  private readonly dataEffectOperationTemplate?: TemplateRef<unknown>;
  @ViewChild('dataEffectDescriptionTemplate', { static: true })
  private readonly dataEffectDescriptionTemplate?: TemplateRef<unknown>;
  @ViewChild('dataRecordKindTemplate', { static: true })
  private readonly dataRecordKindTemplate?: TemplateRef<unknown>;
  @ViewChild('dataRecordStateTemplate', { static: true })
  private readonly dataRecordStateTemplate?: TemplateRef<unknown>;
  @ViewChild('dataRecordChangedTemplate', { static: true })
  private readonly dataRecordChangedTemplate?: TemplateRef<unknown>;
  @ViewChild('dataRecordDurationTemplate', { static: true })
  private readonly dataRecordDurationTemplate?: TemplateRef<unknown>;
  @ViewChild('dataRecordIdTemplate', { static: true })
  private readonly dataRecordIdTemplate?: TemplateRef<unknown>;

  @ViewChild('storageRecordPathTemplate', { static: true })
  private readonly storageRecordPathTemplate?: TemplateRef<unknown>;
  private activeOperation: { kind: OperationKind; id: string } | null = null;
  private readonly expandedTreeIds = new Set<string>();
  private treeExpandAssignmentId: string | null = null;
  private treeExpandInitialized = false;
  private applyingDetailsTabHash = false;
  private processCatalog: Array<{ key: string; displayName?: string; dataEffects?: unknown; storageEffects?: unknown }> = [];

  ngOnInit(): void {
    this.bindDetailsTabs();
    this.route.paramMap
      .pipe(
        map(params => ({
          kind: (this.route.snapshot.data['operationKind'] ?? 'Assignment') as OperationKind,
          id: params.get('assignmentId') ?? params.get('jobId') ?? '',
        })),
        distinctUntilChanged((left, right) => left.kind === right.kind && left.id === right.id),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ kind, id }) => {
        if (!id) {
          return;
        }
        this.openOperationDetails(kind, id);
      });
    this.route.fragment
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(fragment => this.applyDetailsTabHash(fragment));
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
  }

  ngAfterViewInit(): void {
    this.bindDetailsTabs();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.detailsClosed$.next();
    this.detailsClosed$.complete();
    this.detailsRequestCancelled$.next();
    this.detailsRequestCancelled$.complete();
    this.operationLogRequestCancelled$.next();
    this.operationLogRequestCancelled$.complete();
    this.notificationRequestCancelled$.next();
    this.notificationRequestCancelled$.complete();
    this.callDetailsCancelled$.next();
    this.callDetailsCancelled$.complete();
    this.databaseRequestCancelled$.next();
    this.databaseRequestCancelled$.complete();
    this.clearCopiedCommandTypeReset();
  }

  copyCommandType(commandType: string): void {
    this.copyText(commandType, value => {
      this.copiedCommandType = value;
      this.clearCopiedCommandTypeReset();
      this.copiedCommandTypeReset = setTimeout(() => {
        if (this.copiedCommandType === value) {
          this.copiedCommandType = null;
          this.cdr.markForCheck();
        }
      }, 1500);
    });
  }

  open(kind: OperationKind, operationId: string): void {
    this.openOperationDetails(kind, operationId);
  }

  refreshDetails(silent = false): void {
    if (!this.activeOperation) return;
    if (!silent && this.detailsLoading) return;
    if (silent && (this.detailsLoading || this.detailsSilentLoading)) return;
    this.openOperationDetails(this.activeOperation.kind, this.activeOperation.id, silent);
  }

  requestCancellation(): void {
    if (!this.selectedAssignment?.id || !this.canCancelCurrent) {
      return;
    }
    this.cancellationVisible = true;
  }

  requestRestart(): void {
    if (!this.selectedAssignment?.id || !this.canRestartCurrent) {
      return;
    }
    this.restartVisible = true;
  }

  confirmRestart(): void {
    const assignmentId = this.selectedAssignment?.id;
    if (!assignmentId || this.restartSaving) {
      return;
    }
    this.restartSaving = true;
    this.errorMessage = '';
    this.assignments.assignmentsAssignmentIdRestartPost(assignmentId)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.detailsClosed$),
        finalize(() => {
          this.restartSaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.restartVisible = false;
          this.refreshDetails();
        },
        error: error => {
          this.errorMessage = assignmentStartApiError(error, 'Failed to restart assignment');
        },
      });
  }

  confirmCancellation(): void {
    const assignmentId = this.selectedAssignment?.id;
    if (!assignmentId || this.cancellationSaving) {
      return;
    }
    this.cancellationSaving = true;
    this.errorMessage = '';
    this.assignments.assignmentsAssignmentIdCancelPost(assignmentId)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.detailsClosed$),
        finalize(() => {
          this.cancellationSaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.cancellationVisible = false;
          this.refreshDetails();
        },
        error: () => {
          this.errorMessage = 'Failed to cancel assignment';
        },
      });
  }

  requestCopy(): void {
    if (!this.selectedAssignment?.id) return;
    this.copyVisible = true;
  }

  requestMakeSubscription(): void {
    const assignment = this.selectedAssignment;
    if (!assignment?.id) return;
    this.subscriptionDialog()?.open(
      assignment.id,
      defaultSubscriptionNameFromAssignment({
        executionDefinitionKey: assignment.executionDefinitionKey,
        safeInputJson: assignment.safeInputJson,
      }),
    );
  }

  requestNotifyPeople(): void {
    const assignment = this.selectedAssignment;
    if (!assignment) {
      return;
    }
    const failed = (assignment.state ?? '').toLowerCase() === 'failed';
    void this.router.navigate([NOTIFY_PEOPLE_PATH], {
      queryParams: notifyPeopleQueryParams({
        eventKey: failed ? 'assignment.failed' : 'assignment.completed',
        sourceProcessDefinitionKey: assignment.executionDefinitionKey,
        priority: failed ? 'Critical' : 'Normal',
      }),
    });
  }

  confirmCopy(): void {
    const sourceId = this.selectedAssignment?.id;
    if (!sourceId) return;
    this.copySaving = true;
    this.errorMessage = '';
    this.assignments.assignmentsAssignmentIdCopyPost(sourceId)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.detailsClosed$),
        finalize(() => {
          this.copySaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: response => {
          this.copyVisible = false;
          if (response.assignmentId) {
            this.openRelatedAssignment(response.assignmentId);
          }
        },
        error: error => {
          this.errorMessage = this.copyErrorMessage(error);
        },
      });
  }

  openRelatedAssignment(assignmentId: string): void {
    void this.router.navigateByUrl(operationDetailsUrl('Assignment', assignmentId));
  }

  emitShowChildren(assignmentId?: string): void {
    if (!assignmentId) return;
    void this.router.navigate(['/admin/orchestration/monitoring'], {
      queryParams: { kind: 'Assignment', causationId: assignmentId },
    });
  }

  toggleRequestTreeNode(node: RequestTreeNode, event?: Event): void {
    event?.stopPropagation();
    if (node.kind !== 'command' && node.kind !== 'method') {
      return;
    }
    toggleTreeId(this.expandedTreeIds, node.id);
    this.syncRequestStepTreeExpand();
  }

  selectRequestTreeRow(node: RequestTreeNode): void {
    this.selectedRowId = node.id;
    if (node.callId) {
      this.applySelectedCommandDetail(null);
      this.loadExternalCallDetails(node.callId);
      return;
    }
    this.expandedCallId = null;
    this.applySelectedCallDetails(null);
    this.applySelectedCommandDetail(node);
    this.callDetailsCancelled$.next();
    this.cdr.markForCheck();
  }

  toggleSatelliteRegions(): void {
    if (this.satelliteRegions.length === 0) return;
    this.satelliteRegionsExpanded = !this.satelliteRegionsExpanded;
    this.cdr.markForCheck();
  }

  onDetailsTabChange(tabId: string): void {
    this.setDetailsTab(tabId);
    this.syncDetailsTabHash(tabId);
  }

  private applyDetailsTabHash(fragment: string | null | undefined): void {
    const tab = operationDetailsTabFromHash(fragment);
    this.applyingDetailsTabHash = true;
    try {
      this.setDetailsTab(tab);
    } finally {
      this.applyingDetailsTabHash = false;
    }
    if (!adminHashesEqual(operationDetailsTabHash(tab), fragment)) {
      this.syncDetailsTabHash(tab);
    }
  }

  private setDetailsTab(tabId: string): void {
    let tab = canonicalizeOperationDetailsTab(tabId) ?? 'overview';
    if ((tab === 'events' || tab === 'notifications') && !this.selectedAssignment) {
      tab = 'overview';
    }
    if (this.activeDetailsTab === tab) {
      return;
    }
    this.activeDetailsTab = tab;
    if (tab === 'logs') {
      this.loadOperationLogs();
    }
    if (tab === 'notifications') {
      this.loadAssignmentNotifications();
    }
    this.cdr.markForCheck();
  }

  private syncDetailsTabHash(tabId: string): void {
    if (this.applyingDetailsTabHash) {
      return;
    }
    const fragment = operationDetailsTabHash(tabId);
    if (adminHashesEqual(fragment, this.route.snapshot.fragment)) {
      return;
    }
    const tree = this.router.parseUrl(this.router.url);
    tree.fragment = fragment;
    void this.router.navigateByUrl(tree);
  }

  loadOperationLogs(silent = false): void {
    if (!this.activeOperation) return;
    this.operationLogRequestCancelled$.next();
    if (!silent) this.operationLogsLoading = true;
    this.monitoringService.monitoringOperationsKindOperationIdLogsGet(
      this.activeOperation.kind,
      this.activeOperation.id,
      undefined,
      undefined,
      this.operationLogLevel || undefined,
      this.operationLogSearch || undefined,
      200,
    ).pipe(
      takeUntil(this.destroy$),
      takeUntil(this.detailsClosed$),
      takeUntil(this.operationLogRequestCancelled$),
      finalize(() => {
        if (!silent) this.operationLogsLoading = false;
        this.cdr.markForCheck();
      }),
    ).subscribe({
      next: response => {
        this.operationLogViews = (response.entries ?? []).map(entry => this.toLogView(entry));
        this.operationLogsCopyText = formatOperationLogsForCopy(this.operationLogViews);
        this.operationLogsAvailable = response.available ?? false;
        this.operationLogsProvider = response.provider ?? '';
        this.operationLogsMessage = response.statusMessage ?? '';
        this.operationLogsExternalUrl = this.safeExternalUrl(response.externalViewerUrl);
        this.operationLogsHasMore = response.hasMore ?? false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.operationLogViews = [];
        this.operationLogsCopyText = '';
        this.operationLogsAvailable = false;
        this.operationLogsMessage = 'Failed to load operation logs';
      },
    });
  }

  private openOperationDetails(
    kind: OperationKind,
    operationId: string,
    silent = false,
  ): void {
    const keepContent =
      this.activeOperation?.kind === kind &&
      this.activeOperation.id === operationId;
    this.pageTitleKey = kind === 'JobExecution' ? 'JobExecution' : 'Assignment details';
    this.pageSubtitle = null;
    this.executedOnLabel = null;
    this.executedOnService = null;
    this.executedOnHost = null;
    this.executedOnWorkerId = null;
    this.parentRoute = kind === 'JobExecution'
      ? '/admin/orchestration/monitoring'
      : '/admin/orchestration/assignments';
    if (silent) {
      this.detailsRequestCancelled$.next();
      this.detailsSilentLoading = true;
    } else {
      this.detailsClosed$.next();
      this.detailsRequestCancelled$.next();
      this.detailsLoading = true;
    }
    this.activeOperation = { kind, id: operationId };
    if (!keepContent && !silent) {
      this.selectedAssignment = null;
      this.selectedJob = null;
      this.childOperations = [];
      this.resetOperationLogs();
    }
    this.monitoringService
      .monitoringOperationsKindOperationIdGet(kind, operationId)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.detailsClosed$),
        takeUntil(this.detailsRequestCancelled$),
        finalize(() => {
          if (silent) {
            this.detailsSilentLoading = false;
          } else {
            this.detailsLoading = false;
          }
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: details => {
          this.selectedAssignment = details.assignment ?? null;
          this.selectedJob = details.job ?? null;
          this.applyJobDetails(this.selectedJob);
          this.applyAssignmentDetails(this.selectedAssignment, silent);
          this.applySatelliteParameters(this.selectedAssignment);
          this.applyRecordHeading();
          if (this.selectedAssignment?.id) {
            this.loadChildOperations(this.selectedAssignment.id);
          }
          if (!silent) {
            this.startOperationLogPolling(this.shouldPollDetails(details.state));
            this.startDetailsPolling(this.shouldPollDetails(details.state));
          }
        },
        error: () => {
          this.errorMessage = 'Failed to load operation details';
        },
      });
  }

  private loadChildOperations(assignmentId: string): void {
    this.childrenLoading = true;
    this.monitoringService.monitoringOperationsGet(
      'Assignment', undefined, undefined, undefined, undefined, undefined, assignmentId,
      undefined, undefined, undefined, 1, 100,
    ).pipe(
      takeUntil(this.destroy$),
      takeUntil(this.detailsClosed$),
      finalize(() => {
        this.childrenLoading = false;
        this.cdr.markForCheck();
      }),
    ).subscribe({
      next: response => {
        this.childOperations = response.items ?? [];
        this.applyChildChainLinks(this.childOperations);
      },
      error: () => {
        this.childOperations = [];
      },
    });
  }

  private applyJobDetails(
    job: (BackgroundJobExecutionDetailsResponse & { safeConfigJson?: string | null }) | null,
  ): void {
    this.jobConfigJson = this.formatJson(job?.safeConfigJson);
    this.jobResultJson = this.formatJson(job?.resultJson);
    this.jobResultTypeName = job?.resultTypeName || null;
    this.jobStateSeverity = this.getStateSeverity(job?.state);
  }

  private applyAssignmentDetails(
    assignment: AssignmentAuditDetailsResponse | null,
    silent = false,
  ): void {
    const expandedCallId = this.expandedCallId;
    const selectedCallDetails = this.selectedCallDetails;
    const selectedRowId = this.selectedRowId;
    if (!silent) {
      this.resetAssignmentDetails();
    }
    if (!assignment) {
      this.resetRequestStepTreeExpand();
      return;
    }
    const externalCalls = this.toExternalCallViews(assignment);
    this.expandedCallId = externalCalls.some(call => call.id === expandedCallId) ? expandedCallId : null;
    this.applySelectedCallDetails(this.expandedCallId === expandedCallId ? selectedCallDetails : null);
    this.selectedRowId = selectedRowId;

    this.isPostProcessingAssignment = assignment.executionDefinitionKey === 'satellite.create-product-postprocessing';
    const processKey = assignment.executionDefinitionKey?.trim() || '';
    this.assignmentProcessKey = processKey || '—';
    const processVersion = (assignment as { executionDefinitionVersion?: number }).executionDefinitionVersion;
    this.assignmentProcessVersion = typeof processVersion === 'number' ? processVersion : null;
    this.assignmentProcessUrl = processKey ? orchestrationProcessUrl(processKey) : null;
    this.assignmentStateLabel = assignment.state ?? '—';
    this.assignmentStateSeverity = this.getStateSeverity(assignment.state);
    this.canCancelCurrent = canCancelAssignment(assignment.state);
    this.canRestartCurrent = canRestartAssignment(assignment.state);
    this.parentAssignmentId = assignment.causationId || null;
    this.satelliteProductId = readSatelliteProductId(this.parseJsonObject(assignment.safeInputJson));
    this.satelliteProductAnalysisUrl = satelliteProductAnalysisUrlFromJson(assignment.safeInputJson);
    this.sourceSubscriptionUrl = orchestrationSubscriptionUrl(assignment.sourceSubscriptionId);
    const assignmentInputJson = this.formatJson(assignment.safeInputJson);
    this.assignmentInputJson = assignmentInputJson === '—' ? null : assignmentInputJson;
    const assignmentOutput = extractMonitoredOutput(assignment.safeOutputJson);
    this.assignmentResultJson = assignmentOutput.result === '—' ? null : assignmentOutput.result;
    const executions = (assignment.commandExecutions ?? [])
      .slice()
      .sort((left, right) => this.compareCommandOrder(left, right));
    const commandIds = new Set(executions.map(execution => execution.id).filter((id): id is string => !!id));
    this.commandViews = executions.map(execution => {
        const output = extractMonitoredOutput(execution.outputSummaryJson);
        const commandType = execution.commandType ?? '—';
        const causationId = execution.causationId ?? null;
        return {
          id: execution.id ?? '',
          stepKey: execution.stepKey ?? '—',
          commandType,
          commandTypeShort: this.shortCommandType(commandType),
          attempt: execution.attempt ?? '—',
          state: execution.state ?? '—',
          severity: this.getStateSeverity(execution.state),
          startedAt: execution.startedAt ?? undefined,
          duration: this.formatExecutionDuration(execution),
          workerId: execution.workerId ?? '—',
          inputJson: this.formatJson(execution.inputSummaryJson),
          resultJson: output.result,
          logs: output.logs,
          error: execution.error
            ? `${execution.errorCategory ? execution.errorCategory + ': ' : ''}${execution.error}`
            : null,
          parentCommandId: causationId && commandIds.has(causationId) ? causationId : null,
        };
      });
    if (
      assignmentOutput.logs.length
      && !this.commandViews.some(command => command.stepKey === 'StartFindSatelliteProductsCommand')
    ) {
      this.commandViews = [
        {
          id: `${assignment.id ?? 'assignment'}-start`,
          stepKey: 'StartFindSatelliteProductsCommand',
          commandType: 'StartFindSatelliteProductsCommand',
          commandTypeShort: 'StartFindSatelliteProductsCommand',
          attempt: 1,
          state: 'Succeeded',
          severity: 'success',
          startedAt: assignment.createdAt ?? assignment.startedAt ?? undefined,
          duration: '—',
          workerId: '—',
          inputJson: assignmentInputJson,
          resultJson: assignmentOutput.result,
          logs: assignmentOutput.logs,
          error: null,
          parentCommandId: null,
        },
        ...this.commandViews,
      ];
    }
    this.applyRequestStepTree(assignment.id ?? null, externalCalls);
    this.applyOnnxModel(assignment, executions);
    this.eventViews = (assignment.publishedEvents ?? []).map(event => ({
      id: event.id ?? '',
      kind: event.kind || '—',
      eventKey: event.eventKey || '—',
      eventUrl: orchestrationEventUrl(event.eventKey),
      eventId: event.eventId || '',
      identifierLabel: publishedEventIdentifierLabel(event.eventKey),
      triggerName: event.triggerName || '—',
      state: event.state ?? '—',
      severity: this.getStateSeverity(event.state),
      startedAt: event.startedAt ?? undefined,
      completedAt: event.completedAt ?? undefined,
      targetAssignmentId: event.targetAssignmentId || null,
      targetAssignmentUrl: event.targetAssignmentId
        ? operationDetailsUrl('Assignment', event.targetAssignmentId)
        : null,
      parametersJson: event.safeParametersJson ? this.formatJson(event.safeParametersJson) : null,
      error: event.error || null,
      productAnalysisUrl: satelliteProductAnalysisUrlFromEvent(event.eventKey, event.eventId),
    }));
    this.applyFollowUpAssignments();
    this.historyViews = (assignment.stateHistory ?? []).map(transition => ({
      id: transition.id ?? '',
      state: transition.state ?? '—',
      severity: this.getStateSeverity(transition.state),
      changedAt: transition.changedAt ?? undefined,
      details: transition.details || null,
    }));
    this.applyAssignmentChain(assignment, this.childOperations);
    if (!silent) {
      this.applyAssignmentDatabase(assignment, []);
      this.bindDetailsTabs();
    }
    this.loadAssignmentDatabase(assignment, silent);
    this.loadAssignmentNotifications();
  }

  loadAssignmentNotifications(): void {
    const assignmentId = this.selectedAssignment?.id;
    this.notificationRequestCancelled$.next();
    if (!assignmentId) {
      this.notificationRows = [];
      this.notificationsLoading = false;
      this.cdr.markForCheck();
      return;
    }
    this.notificationsLoading = true;
    this.cdr.markForCheck();
    this.notificationsApi.getJournal({ targetEntityId: assignmentId }).pipe(
      takeUntil(this.notificationRequestCancelled$),
      finalize(() => {
        this.notificationsLoading = false;
        this.bindDetailsTabs();
        this.cdr.markForCheck();
      }),
    ).subscribe({
      next: rows => {
        this.notificationRows = rows;
      },
      error: () => {
        this.notificationRows = [];
      },
    });
  }

  private applyRecordHeading(): void {
    const assignment = this.selectedAssignment;
    if (assignment) {
      const key = assignment.executionDefinitionKey ?? '';
      const heading = toRecordPageHeading({
        meaning: namedKeyLabel(this.processCatalog, key),
        key,
        id: assignment.id,
        fallbackTitle: 'Assignment details',
      });
      this.pageTitleKey = heading.title;
      this.showAssignmentProcessKeyLink = !!this.assignmentProcessUrl
        && this.assignmentProcessKey !== heading.title;
      this.pageSubtitle = this.showAssignmentProcessKeyLink
        ? (assignment.id?.trim() || null)
        : heading.subtitle;
      this.executedOnLabel = assignmentHostLabel({
        kind: 'Assignment',
        startedAt: assignment.startedAt,
        ...readAssignmentHost(assignment),
      });
      const worker = readAssignmentWorker(assignment);
      this.executedOnService = worker.workerServiceName ?? null;
      this.executedOnHost = worker.workerHostName ?? null;
      this.executedOnWorkerId = worker.workerId ?? null;
      return;
    }
    const job = this.selectedJob;
    if (job) {
      const heading = toRecordPageHeading({
        meaning: job.jobName || job.jobType,
        key: job.jobType,
        id: job.id,
        fallbackTitle: 'JobExecution',
      });
      this.pageTitleKey = heading.title;
      this.pageSubtitle = heading.subtitle;
      this.executedOnLabel = null;
      this.executedOnService = null;
      this.executedOnHost = null;
      this.executedOnWorkerId = null;
      return;
    }
    this.pageTitleKey = this.activeOperation?.kind === 'JobExecution'
      ? 'JobExecution'
      : 'Assignment details';
    this.pageSubtitle = null;
    this.executedOnLabel = null;
    this.executedOnService = null;
    this.executedOnHost = null;
    this.executedOnWorkerId = null;
  }

  private applyAssignmentChain(
    assignment: AssignmentAuditDetailsResponse,
    children: MonitoredOperationListItemResponse[],
  ): void {
    const chain: AssignmentChainLink[] = [];
    if (assignment.causationId) {
      chain.push({
        id: assignment.causationId,
        title: 'Parent assignment',
        detail: assignment.causationId,
        state: '—',
        severity: 'secondary',
        kind: 'parent',
        url: operationDetailsUrl('Assignment', assignment.causationId),
      });
    }
    chain.push({
      id: assignment.id ?? '',
      title: 'This assignment',
      detail: assignment.executionDefinitionKey || assignment.id || '—',
      state: assignment.state ?? '—',
      severity: this.getStateSeverity(assignment.state),
      kind: 'current',
      url: null,
      detailUrl: this.assignmentProcessUrl,
      hostLabel: assignmentHostLabel({
        kind: 'Assignment',
        startedAt: assignment.startedAt,
        ...readAssignmentHost(assignment),
      }),
      ...readAssignmentWorker(assignment),
    });
    for (const child of children) {
      if (!child.id) continue;
      chain.push({
        id: child.id,
        title: child.operationType || 'Child assignments',
        detail: child.id,
        state: child.state ?? '—',
        severity: this.getStateSeverity(child.state),
        kind: 'child',
        url: operationDetailsUrl('Assignment', child.id),
        hostLabel: assignmentHostLabel({
          kind: child.kind ?? 'Assignment',
          startedAt: child.startedAt,
          ...readAssignmentHost(child),
        }),
        ...readAssignmentWorker(child),
      });
    }
    for (const event of this.eventViews) {
      if (!event.targetAssignmentId) continue;
      chain.push({
        id: event.targetAssignmentId,
        title: event.eventKey,
        detail: event.targetAssignmentId,
        state: event.state,
        severity: event.severity,
        kind: 'event',
        url: operationDetailsUrl('Assignment', event.targetAssignmentId),
      });
    }
    this.assignmentChain = chain;
  }

  private applyChildChainLinks(children: MonitoredOperationListItemResponse[]): void {
    if (!this.selectedAssignment) return;
    this.applyAssignmentChain(this.selectedAssignment, children);
    this.cdr.markForCheck();
  }

  private compareCommandOrder(left: CommandExecutionResponse, right: CommandExecutionResponse): number {
    const leftTime = left.startedAt ? new Date(left.startedAt).getTime() : 0;
    const rightTime = right.startedAt ? new Date(right.startedAt).getTime() : 0;
    if (leftTime !== rightTime) return leftTime - rightTime;
    return (left.attempt ?? 0) - (right.attempt ?? 0);
  }

  private applyOnnxModel(
    assignment: AssignmentAuditDetailsResponse,
    executions: CommandExecutionResponse[],
  ): void {
    this.onnxModelCancelled$.next();
    const logs = executions.flatMap(execution => {
      try {
        const parsed = JSON.parse(execution.outputSummaryJson || '{}') as { Logs?: unknown; logs?: unknown };
        const lines = parsed.Logs ?? parsed.logs;
        return Array.isArray(lines) ? lines.filter((line): line is string => typeof line === 'string') : [];
      } catch {
        return [];
      }
    });
    const model = readOnnxAssignmentModel(
      assignment.executionDefinitionKey,
      assignment.safeOutputJson,
      (assignment.externalServiceCalls ?? []).map(call => call.url),
      logs,
    );
    this.onnxModel = model;
    if (!model?.id) {
      return;
    }
    const modelId = model.id;
    this.aiModels.aiInferenceAiModelGetByIdIdGet(modelId).pipe(
      takeUntil(this.onnxModelCancelled$),
      takeUntil(this.destroy$),
    ).subscribe({
      next: dto => {
        if (this.onnxModel?.id !== modelId) {
          return;
        }
        this.onnxModel = {
          id: modelId,
          code: dto.code?.trim() || this.onnxModel.code,
          name: dto.name?.trim() || this.onnxModel.name,
          version: dto.version?.trim() || this.onnxModel.version,
          fileName: dto.onnxFileName?.trim() || this.onnxModel.fileName,
          url: this.onnxModel.url,
        };
        this.cdr.markForCheck();
      },
      error: () => this.cdr.markForCheck(),
    });
  }

  private shortCommandType(commandType: string): string {
    const parts = commandType.split('.');
    return parts[parts.length - 1] || commandType;
  }

  private resetAssignmentDetails(): void {
    this.requestStepTree = [];
    this.selectedRowId = null;
    this.applySelectedCommandDetail(null);
    this.expandedCallId = null;
    this.applySelectedCallDetails(null);
    this.callDetailsLoading = false;
    this.commandViews = [];
    this.assignmentInputJson = null;
    this.assignmentResultJson = null;
    this.assignmentChain = [];
    this.eventViews = [];
    this.followUpAssignments = [];
    this.createdArtifactFiles = [];
    this.createdArtifactTables = [];
    this.notificationRows = [];
    this.sourceSubscriptionUrl = null;
    this.historyViews = [];
    this.isPostProcessingAssignment = false;
    this.assignmentStateLabel = '—';
    this.assignmentStateSeverity = 'secondary';
    this.canCancelCurrent = false;
    this.canRestartCurrent = false;
    this.assignmentProcessKey = '—';
    this.assignmentProcessVersion = null;
    this.assignmentProcessUrl = null;
    this.showAssignmentProcessKeyLink = false;
    this.parentAssignmentId = null;
    this.satelliteProductAnalysisUrl = null;
    this.satelliteProductId = null;
    this.onnxModelCancelled$.next();
    this.onnxModel = null;
    this.dataEffectRows = [];
    this.storageEffectRows = [];
    this.storageRecordRows = [];
    this.storageBytesLabel = '—';
    this.dataRecordBytesLabel = '—';
    this.dataRecordGroups = [];
    this.dataRecordRows = [];
    this.filteredDataRecordRows = [];
    this.dataRecordFilterModel = emptyAssignmentDataRecordFilter();
    syncAssignmentDataRecordFilterOptions(this.dataRecordFilterFields, []);
    this.dataRecordCount = 0;
    this.satelliteDownloadInProgress = false;
    this.databaseLoading = false;
  }

  private applyRequestStepTree(assignmentId: string | null, calls: ExternalCallTreeLeaf[]): void {
    if (assignmentId !== this.treeExpandAssignmentId) {
      this.resetRequestStepTreeExpand();
      this.treeExpandAssignmentId = assignmentId;
    }
    this.requestStepTree = buildRequestStepTree(
      this.commandViews.map(command => ({
        id: command.id,
        title: command.stepKey,
        state: command.state,
        severity: command.severity,
        logs: command.logs,
        inputJson: command.inputJson,
        resultJson: command.resultJson,
        attempt: command.attempt,
        startedAt: command.startedAt,
        duration: command.duration,
        workerId: command.workerId,
        commandTypeShort: command.commandTypeShort,
        error: command.error,
        parentCommandId: command.parentCommandId,
      })),
      calls,
      this.treeExpandInitialized ? this.expandedTreeIds : null,
    );
    if (!this.treeExpandInitialized) {
      collectExpandableIds(this.requestStepTree, this.expandedTreeIds);
      this.treeExpandInitialized = this.requestStepTree.length > 0;
    }
    if (!this.selectedRowId) {
      return;
    }
    const selected = findRequestTreeNode(this.requestStepTree, this.selectedRowId);
    if (!selected) {
      this.selectedRowId = null;
      this.applySelectedCommandDetail(null);
      this.expandedCallId = null;
      this.applySelectedCallDetails(null);
      return;
    }
    this.applySelectedCommandDetail(selected);
  }

  private applySelectedCommandDetail(node: RequestTreeNode | null): void {
    this.selectedCommandDetail = node?.kind === 'command'
      ? {
          inputJson: node.inputJson,
          resultJson: node.resultJson,
          logs: node.logs,
          logText: node.logs.join('\n'),
          attempt: node.attempt,
          startedAt: node.startedAt,
          duration: node.duration,
          workerId: node.workerId,
          commandTypeShort: node.commandTypeShort,
          error: node.error,
          satelliteProductUrl: satelliteProductAnalysisUrlFromJson(node.inputJson),
          satelliteProductId: readSatelliteProductId(this.parseJsonObject(node.inputJson)),
        }
      : null;
  }

  private syncRequestStepTreeExpand(): void {
    this.requestStepTree = applyRequestTreeExpand(this.requestStepTree, this.expandedTreeIds);
    this.cdr.markForCheck();
  }

  private resetRequestStepTreeExpand(): void {
    this.expandedTreeIds.clear();
    this.treeExpandAssignmentId = null;
    this.treeExpandInitialized = false;
  }

  private loadExternalCallDetails(callId: string): void {
    if (this.expandedCallId === callId && this.selectedCallDetails) {
      this.cdr.markForCheck();
      return;
    }
    this.expandedCallId = callId;
    this.applySelectedCallDetails(null);
    this.callDetailsLoading = true;
    this.cdr.markForCheck();
    this.callDetailsCancelled$.next();
    this.externalCallsApi.externalServiceCallsIdGet(callId).pipe(
      takeUntil(this.destroy$),
      takeUntil(this.detailsClosed$),
      takeUntil(this.callDetailsCancelled$),
      finalize(() => {
        this.callDetailsLoading = false;
        this.cdr.markForCheck();
      }),
    ).subscribe({
      next: details => {
        this.applySelectedCallDetails(details);
      },
      error: () => {
        this.errorMessage = 'Failed to load call details';
        this.expandedCallId = null;
      },
    });
  }

  private toExternalCallViews(assignment: AssignmentAuditDetailsResponse): ExternalCallTreeLeaf[] {
    return (assignment.externalServiceCalls ?? [])
      .filter(call => !!call.id)
      .map(call => {
        const statusCode = call.statusCode ?? null;
        const url = call.url || '—';
        return {
          id: call.id ?? '',
          serviceName: call.serviceName || '—',
          httpMethod: call.httpMethod || '—',
          url,
          urlLabel: externalCallUrlWithoutQuery(url),
          durationMs: call.durationMs ?? 0,
          proxyLabel: externalCallProxy(call),
          error: call.error || null,
          commandExecutionId: call.commandExecutionId || null,
          statusLabel: statusCode != null ? String(statusCode) : (call.error ? 'Error' : '—'),
          severity: this.callStatusSeverity(statusCode, !!call.succeeded, call.error),
        };
      });
  }

  private callStatusSeverity(
    statusCode: number | null,
    succeeded: boolean,
    error?: string | null,
  ): TagSeverity {
    if (error) return 'danger';
    if (statusCode == null) return 'secondary';
    if (succeeded) return 'success';
    if (statusCode >= 400 && statusCode < 500) return 'warn';
    return 'danger';
  }

  private applySelectedCallDetails(details: ExternalServiceCallDetails | null): void {
    this.selectedCallDetails = details;
    if (!details) {
      this.selectedCallIo = null;
      return;
    }
    const selected = this.selectedRowId
      ? findRequestTreeNode(this.requestStepTree, this.selectedRowId)
      : null;
    const requestUrl = details.url || selected?.url;
    const queryJson = formatHttpQueryParametersJson(requestUrl);
    const capturedBody = hasCapturedHttpBody(details.requestBody)
      ? prettyJson(details.requestBody)
      : null;
    const error = details.error?.trim() || selected?.error?.trim() || null;
    this.selectedCallIo = {
      requestHeaders: prettyJson(details.requestHeadersJson),
      requestBody: capturedBody ?? queryJson,
      requestText: joinCopyableLogParts(
        formatHttpRequestLine(
          details.httpMethod || selected?.httpMethod,
          requestUrl,
        ),
        prettyJson(details.requestHeadersJson),
        capturedBody ?? queryJson ?? this.translate.instant('Body was not captured'),
      ),
      responseHeaders: prettyJson(details.responseHeadersJson),
      responseBody: details.responseBody ? prettyJson(details.responseBody) : null,
      error,
      proxyLabel: externalCallProxy(details) ?? this.translate.instant('Direct'),
      responseText: joinCopyableLogParts(
        error,
        prettyJson(details.responseHeadersJson),
        details.responseBody
          ? prettyJson(details.responseBody)
          : this.translate.instant('Body was not captured'),
      ),
    };
  }

  private formatJson(value?: string | null): string {
    return prettyJson(value);
  }

  private getStateSeverity(state?: string | null): TagSeverity {
    switch (state) {
      case 'Completed':
      case 'Succeeded':
        return 'success';
      case 'Running':
        return 'info';
      case 'Pending':
      case 'Warning':
        return 'warn';
      case 'Failed':
      case 'Error':
      case 'Critical':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  private formatDuration(seconds?: number | null): string {
    if (seconds === undefined || seconds === null) {
      return '—';
    }
    if (seconds < 1) {
      return `${Math.max(0, Math.round(seconds * 1000))} ms`;
    }
    if (seconds < 60) {
      return `${Math.round(seconds)} s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes} min ${remainingSeconds} s`;
  }

  private formatExecutionDuration(execution: CommandExecutionResponse): string {
    if (!execution.startedAt || !execution.completedAt) {
      return '—';
    }
    return this.formatDuration(
      (new Date(execution.completedAt).getTime() - new Date(execution.startedAt).getTime()) / 1000,
    );
  }

  private applySatelliteParameters(assignment: AssignmentAuditDetailsResponse | null): void {
    this.resetSatelliteParameters();
    const parameters = readSatelliteOverviewParameters(assignment);
    if (!parameters) return;

    const archive = readSatelliteArchiveSizes(assignment ?? {});
    const sizeRows = processKeysMatch(assignment?.executionDefinitionKey, SATELLITE_MEASURE_DOWNLOAD_BACKLOG_KEY)
      ? satelliteMeasureBacklogSizeRows(assignment ?? {})
      : [
        { label: 'Found size', value: formatArchiveSizeGiB(archive.estimatedSizeInBytes) },
        { label: 'Downloaded size', value: formatArchiveSizeGiB(archive.downloadedSizeInBytes) },
      ];
    this.satelliteParameterRows = [
      { label: 'Name', value: parameters.name ?? '—' },
      { label: 'Geometry source', value: parameters.geometrySource ?? '—' },
      { label: 'Providers', value: parameters.providers ?? '—' },
      { label: 'Search period', value: this.searchPeriod(parameters.startDate, parameters.endDate) },
      { label: 'Maximum cloud cover', value: parameters.maximumCloudCover?.toString() ?? '—' },
      ...sizeRows,
    ];
    this.satelliteRegionCount = parameters.regionCount?.toString() ?? '—';
    const regionIds = parameters.regionIds;
    this.satelliteRegions = regionIds.map(id => ({ id, name: id }));
    this.satelliteRegionsExpanded = regionIds.length > 0;
    this.loadSatelliteRegionNames(regionIds);
  }

  private resetSatelliteParameters(): void {
    this.satelliteParameterRows = [];
    this.satelliteProductAnalysisUrl = null;
    this.satelliteProductId = null;
    this.satelliteRegions = [];
    this.satelliteRegionCount = '—';
    this.satelliteRegionsExpanded = false;
  }

  private parseJsonObject(value?: string | null): Record<string, unknown> | null {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : null;
    } catch {
      return null;
    }
  }

  private loadSatelliteRegionNames(regionIds: string[]): void {
    if (regionIds.length === 0) return;

    forkJoin(regionIds.map(id =>
      this.regions.dictionariesRegionGetByIdIdGet(id).pipe(
        map(region => ({
          id,
          name: this.serverTranslate.getDisplayValue(region, 'names') || id,
        })),
        catchError(() => of({ id, name: id })),
      ),
    )).pipe(
      takeUntil(this.destroy$),
      takeUntil(this.detailsClosed$),
    ).subscribe(regions => {
      this.satelliteRegions = regions;
      this.cdr.markForCheck();
    });
  }

  private startOperationLogPolling(running: boolean): void {
    if (this.activeDetailsTab === 'logs') {
      this.loadOperationLogs();
    }
    if (!running) return;
    interval(5000).pipe(
      takeUntil(this.destroy$),
      takeUntil(this.detailsClosed$),
    ).subscribe(() => {
      if (this.activeDetailsTab === 'logs') {
        this.loadOperationLogs(true);
      }
    });
  }

  private startDetailsPolling(active: boolean): void {
    if (!active) return;
    interval(5000).pipe(
      takeUntil(this.destroy$),
      takeUntil(this.detailsClosed$),
      takeWhile(() => this.shouldPollCurrentAssignment()),
    ).subscribe(() => this.refreshDetails(true));
  }

  private shouldPollDetails(state?: string | null): boolean {
    return state === 'Pending' || state === 'Running' || state === 'Cancelling';
  }

  private shouldPollCurrentAssignment(): boolean {
    if (this.selectedJob) {
      return this.shouldPollDetails(this.selectedJob.state);
    }
    return this.shouldPollDetails(this.selectedAssignment?.state ?? this.assignmentStateLabel);
  }

  private bindDetailsTabs(): void {
    this.dataEffectColumns = [
      {
        field: 'commandLabel',
        header: 'Command',
        type: 'custom',
        customTemplate: this.dataEffectCommandTemplate,
      },
      { field: 'database', header: 'Database', type: 'text' },
      { field: 'table', header: 'Table', type: 'text' },
      {
        field: 'operation',
        header: 'Operation',
        type: 'custom',
        customTemplate: this.dataEffectOperationTemplate,
      },
      {
        field: 'description',
        header: 'Description',
        type: 'custom',
        customTemplate: this.dataEffectDescriptionTemplate,
      },
    ];
    this.storageEffectColumns = [
      {
        field: 'commandLabel',
        header: 'Command',
        type: 'custom',
        customTemplate: this.dataEffectCommandTemplate,
      },
      { field: 'bucket', header: 'Bucket', type: 'text' },
      { field: 'objectKind', header: 'Object kind', type: 'text' },
      {
        field: 'operation',
        header: 'Operation',
        type: 'custom',
        customTemplate: this.dataEffectOperationTemplate,
      },
      {
        field: 'description',
        header: 'Description',
        type: 'custom',
        customTemplate: this.dataEffectDescriptionTemplate,
      },
    ];
    this.dataRecordColumns = [
      { field: 'database', header: 'Database', type: 'text' },
      { field: 'table', header: 'Table', type: 'text' },
      {
        field: 'kind',
        header: 'Kind',
        type: 'custom',
        customTemplate: this.dataRecordKindTemplate,
      },
      {
        field: 'operation',
        header: 'Operation',
        type: 'custom',
        customTemplate: this.dataEffectOperationTemplate,
      },
      { field: 'label', header: 'Record', type: 'text' },
      { field: 'bytesLabel', header: 'Size', type: 'text' },
      {
        field: 'id',
        header: 'Id',
        type: 'custom',
        customTemplate: this.dataRecordIdTemplate,
      },
      {
        field: 'state',
        header: 'Status',
        type: 'custom',
        customTemplate: this.dataRecordStateTemplate,
      },
      {
        field: 'stateDuration',
        header: 'Time in status',
        type: 'custom',
        customTemplate: this.dataRecordDurationTemplate,
      },
      {
        field: 'changedAt',
        header: 'Changed',
        type: 'custom',
        customTemplate: this.dataRecordChangedTemplate,
      },
    ];
    this.storageRecordColumns = [
      { field: 'bucket', header: 'Bucket', type: 'text' },
      {
        field: 'path',
        header: 'Path',
        type: 'custom',
        customTemplate: this.storageRecordPathTemplate,
      },
      { field: 'objectKind', header: 'Object kind', type: 'text' },
      { field: 'bytesLabel', header: 'Size', type: 'text' },
      { field: 'createdAt', header: 'Created', type: 'date' },
      {
        field: 'id',
        header: 'Id',
        type: 'custom',
        customTemplate: this.dataRecordIdTemplate,
      },
    ];
    const databaseCount = this.dataRecordCount
      || this.storageRecordRows.length
      || this.dataEffectRows.length
      || this.storageEffectRows.length;
    this.detailsTabs = this.selectedAssignment
      ? [
          { id: 'overview', label: 'Overview', template: this.overviewTemplate },
          { id: 'events', label: 'Events', count: this.eventViews.length, template: this.eventsTemplate },
          { id: 'notifications', label: 'Event notifications', count: this.notificationRows.length, template: this.notificationsTemplate },
          { id: 'request-steps', label: 'Request steps', count: countCompletedRequestSteps(this.commandViews), template: this.requestStepsTemplate },
          { id: 'storage', label: 'Storage', count: databaseCount, template: this.databaseTemplate },
          { id: 'logs', label: 'External logs', template: this.logsTemplate },
        ]
      : [
          { id: 'overview', label: 'Overview', template: this.overviewTemplate },
          { id: 'request-steps', label: 'Request steps', count: countCompletedRequestSteps(this.commandViews), template: this.requestStepsTemplate },
          { id: 'storage', label: 'Storage', count: databaseCount, template: this.databaseTemplate },
          { id: 'logs', label: 'External logs', template: this.logsTemplate },
        ];
  }

  private loadAssignmentDatabase(assignment: AssignmentAuditDetailsResponse, silent = false): void {
    this.databaseRequestCancelled$.next();
    if (!silent) {
      this.databaseLoading = true;
    }
    const processKey = assignment.executionDefinitionKey ?? '';
    forkJoin({
      processes: this.assignments.assignmentsProcessesGet().pipe(catchError(() => of([]))),
      products: assignment.id && assignmentHasSatelliteProducts(processKey)
        ? this.assignments.assignmentsAssignmentIdSatelliteProductsGet(assignment.id, 0, 100)
          .pipe(catchError(() => of([])))
        : of([]),
    }).pipe(
      takeUntil(this.destroy$),
      takeUntil(this.detailsClosed$),
      takeUntil(this.databaseRequestCancelled$),
      finalize(() => {
        this.databaseLoading = false;
        this.cdr.markForCheck();
      }),
    ).subscribe(({ processes, products }) => {
      this.processCatalog = (processes as Array<{
        key: string;
        displayName?: string;
        dataEffects?: unknown;
        storageEffects?: unknown;
      }>) ?? [];
      if (this.selectedAssignment?.id !== assignment.id) {
        return;
      }
      const definition = this.processCatalog.find(
        item => processKeysMatch(item.key, processKey),
      );
      const nextEffects = this.toDataEffectRows(normalizeProcessDataEffects(definition?.dataEffects));
      if (!this.sameDataEffectRows(this.dataEffectRows, nextEffects)) {
        this.dataEffectRows = nextEffects;
      }
      const nextStorage = this.toStorageEffectRows(normalizeProcessStorageEffects(definition?.storageEffects));
      if (!this.sameStorageEffectRows(this.storageEffectRows, nextStorage)) {
        this.storageEffectRows = nextStorage;
      }
      this.applyAssignmentDatabase(assignment, products ?? []);
      this.applyRecordHeading();
      if (silent) {
        this.patchDatabaseTabCount();
      } else {
        this.bindDetailsTabs();
      }
      this.cdr.markForCheck();
    });
  }

  private applyAssignmentDatabase(
    assignment: AssignmentAuditDetailsResponse,
    products: Parameters<typeof composeAssignmentDataRecords>[1],
  ): void {
    this.dataRecordGroups = composeAssignmentDataRecords(assignment, products);
    this.dataRecordRows = syncAssignmentDataRecordRows(
      this.dataRecordRows,
      flattenAssignmentDataRecords(this.dataRecordGroups),
    );
    syncAssignmentDataRecordFilterOptions(this.dataRecordFilterFields, this.dataRecordRows);
    this.applyDataRecordFilters();
    this.dataRecordCount = assignmentDataRecordCount(this.dataRecordGroups);
    this.dataRecordBytesLabel = assignmentDataRecordBytesLabel(this.dataRecordGroups);
    this.satelliteDownloadInProgress = hasInProgressSatelliteDownload(this.dataRecordGroups);
    const storageGroups = readAssignmentStorageRecords(assignment);
    this.storageRecordRows = flattenAssignmentStorageRecords(storageGroups);
    this.storageBytesLabel = assignmentStorageBytesLabel(storageGroups);
    this.createdArtifactFiles = assignmentOverviewFiles(this.storageRecordRows);
    this.createdArtifactTables = assignmentOverviewTables(this.dataRecordGroups);
  }

  private applyFollowUpAssignments(): void {
    this.followUpAssignments = assignmentFollowUps(this.eventViews).map(item => ({
      ...item,
      severity: this.getStateSeverity(item.state),
    }));
  }

  applyDataRecordFilters(): void {
    this.filteredDataRecordRows = filterAssignmentDataRecords(
      this.dataRecordRows,
      this.dataRecordFilterModel,
    );
    this.cdr.markForCheck();
  }

  get visibleDataEffectRows(): DataEffectRow[] {
    return filterByCatalogQuery(
      this.dataEffectRows,
      this.dataEffectSearch,
      row => [row.command, row.commandLabel, row.database, row.table, row.operation, row.description],
    );
  }

  get visibleDataRecordRows(): AssignmentDataRecordRow[] {
    return filterByCatalogQuery(
      this.filteredDataRecordRows,
      this.dataRecordSearch,
      row => [row.id, row.label, row.state, row.operation, row.database, row.table, row.kind, row.bytesLabel],
    );
  }

  get visibleStorageEffectRows(): StorageEffectRow[] {
    return filterByCatalogQuery(
      this.storageEffectRows,
      this.storageEffectSearch,
      row => [row.command, row.commandLabel, row.bucket, row.objectKind, row.operation, row.description],
    );
  }

  get visibleStorageRecordRows() {
    return filterByCatalogQuery(
      this.storageRecordRows,
      this.storageRecordSearch,
      row => [row.id, row.path, row.objectKind, row.bucket, row.bytesLabel],
    );
  }

  get visibleEventViews(): PublishedEventView[] {
    return filterByCatalogQuery(
      this.eventViews,
      this.eventSearch,
      event => [event.kind, event.eventKey, event.eventId, event.triggerName, event.state, event.error],
    );
  }

  clearDataRecordFilters(): void {
    this.dataRecordFilterModel = emptyAssignmentDataRecordFilter();
    this.applyDataRecordFilters();
  }

  private patchDatabaseTabCount(): void {
    const count = this.dataRecordCount
      || this.storageRecordRows.length
      || this.dataEffectRows.length
      || this.storageEffectRows.length;
    const storageTab = this.detailsTabs.find(tab => tab.id === 'storage');
    if (storageTab && storageTab.count !== count) {
      storageTab.count = count;
    }
  }

  private sameDataEffectRows(left: DataEffectRow[], right: DataEffectRow[]): boolean {
    return left.length === right.length
      && left.every((row, index) =>
        row.command === right[index].command
        && row.database === right[index].database
        && row.table === right[index].table
        && row.operation === right[index].operation
        && row.description === right[index].description);
  }

  private sameStorageEffectRows(left: StorageEffectRow[], right: StorageEffectRow[]): boolean {
    return left.length === right.length
      && left.every((row, index) =>
        row.command === right[index].command
        && row.bucket === right[index].bucket
        && row.objectKind === right[index].objectKind
        && row.operation === right[index].operation
        && row.description === right[index].description);
  }

  private toStorageEffectRows(effects: ProcessStorageEffect[]): StorageEffectRow[] {
    return effects.map(effect => ({
      ...effect,
      commandLabel: effect.command === '*' ? 'All commands' : effect.command,
      commandUrl: isCodeCatalogEnabled() && effect.command && effect.command !== '*'
        ? developerCatalogCommandLink(effect.command)
        : null,
      operationSeverity: dataEffectOperationSeverity(effect.operation),
    }));
  }

  private toDataEffectRows(effects: ProcessDataEffect[]): DataEffectRow[] {
    return effects.map(effect => ({
      ...effect,
      commandLabel: effect.command === '*' ? 'All commands' : effect.command,
      commandUrl: isCodeCatalogEnabled() && effect.command && effect.command !== '*'
        ? developerCatalogCommandLink(effect.command)
        : null,
      operationSeverity: dataEffectOperationSeverity(effect.operation),
    }));
  }

  private resetOperationLogs(): void {
    this.operationLogViews = [];
    this.operationLogsCopyText = '';
    this.operationLogsLoading = false;
    this.operationLogsAvailable = false;
    this.operationLogsProvider = '';
    this.operationLogsMessage = '';
    this.operationLogsExternalUrl = null;
    this.operationLogsHasMore = false;
  }

  private toLogView(entry: OperationLogRecordResponse): OperationLogView {
    return {
      timestamp: entry.timestamp ?? undefined,
      level: entry.level || '—',
      severity: this.getStateSeverity(entry.level),
      serviceName: entry.serviceName || '—',
      loggerName: entry.loggerName || null,
      message: entry.message ?? null,
      traceId: entry.traceId || null,
      exception: entry.exception || null,
    };
  }

  private safeExternalUrl(value?: string | null): string | null {
    if (!value) return null;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
    } catch {
      return null;
    }
  }

  private copyText(value: string | null | undefined, onCopied: (text: string) => void): void {
    const text = value?.trim();
    if (!text) {
      return;
    }
    void navigator.clipboard.writeText(text).then(() => {
      onCopied(text);
      this.cdr.markForCheck();
    }).catch(() => {
      this.errorMessage = 'Copy failed';
      this.cdr.markForCheck();
    });
  }

  private clearCopiedCommandTypeReset(): void {
    if (this.copiedCommandTypeReset) {
      clearTimeout(this.copiedCommandTypeReset);
      this.copiedCommandTypeReset = null;
    }
  }

  private copyErrorMessage(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) return 'Failed to copy assignment';
    if (typeof error.error === 'string' && error.error.trim()) return error.error;

    const problem = error.error as {
      detail?: unknown;
      title?: unknown;
      errors?: Record<string, unknown>;
    } | null;
    const validationMessage = problem?.errors
      ? Object.values(problem.errors)
        .flatMap(value => Array.isArray(value) ? value : [value])
        .find((value): value is string => typeof value === 'string' && !!value.trim())
      : undefined;

    if (validationMessage) return validationMessage;
    if (typeof problem?.detail === 'string' && problem.detail.trim()) return problem.detail;
    if (typeof problem?.title === 'string' && problem.title.trim()) return problem.title;
    return 'Failed to copy assignment';
  }

  private searchPeriod(startDate?: string | null, endDate?: string | null): string {
    if (!startDate && !endDate) {
      return '—';
    }

    const lang = this.translate.currentLang;
    const start = startDate ? this.dateFormatLang.transform(startDate, lang) ?? startDate : '—';
    const end = endDate ? this.dateFormatLang.transform(endDate, lang) ?? endDate : '—';
    return `${start} — ${end}`;
  }
}
